import { useId } from 'react'

import { Button } from '@atoms'
import { useCopied } from '@hooks/useCopied'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('embeds.editor')

type EmbedCodeBlockProps = {
  label: string
  code: string
  copyLabel: string
  copiedMessage: string
  disabled?: boolean
  emptyMessage?: string
}

export function EmbedCodeBlock({
  label,
  code,
  copyLabel,
  copiedMessage,
  disabled = false,
  emptyMessage,
}: EmbedCodeBlockProps) {
  const [copied, copy] = useCopied()
  const labelId = useId()

  return (
    <div className="tw:overflow-hidden tw:rounded-lg tw:bg-gray-cool-900">
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:border-white/10 tw:py-2 tw:pl-4 tw:pr-2">
        <span id={labelId} className="tw:text-xs tw:font-medium tw:text-gray-cool-300">
          {label}
        </span>
        <Button
          size="sm"
          rank="secondary"
          leadingIcon={copied ? 'check' : 'copy-06'}
          disabled={disabled || !code}
          ariaLabel={copied ? undefined : copyLabel}
          className="tw:min-w-26 tw:justify-center"
          onClick={() => copy(code)}
        >
          {copied ? t('copied') : t('copy')}
        </Button>
      </div>
      {code ? (
        <pre
          aria-labelledby={labelId}
          // Scrollable, so it must be reachable by keyboard.
          tabIndex={0}
          className="tw:m-0 tw:overflow-x-auto tw:whitespace-pre-wrap tw:break-all tw:p-4 tw:text-xs tw:leading-relaxed tw:text-gray-cool-50 tw:focus-visible:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-inset tw:focus-visible:ring-primary-300"
        >
          <code>{code}</code>
        </pre>
      ) : (
        <p className="tw:m-0 tw:p-4 tw:text-xs tw:text-gray-cool-300">{emptyMessage}</p>
      )}
      <span role="status" className="tw:sr-only">
        {copied ? copiedMessage : ''}
      </span>
    </div>
  )
}
